
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Send, AlertCircle, Loader2, Check, Info, Bug, ExternalLink } from "lucide-react";
import { AdminSettings } from "@/types/adminSettings";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// WhatsApp icon component
const BrandWhatsapp = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21" />
    <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1Z" />
    <path d="M14 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1Z" />
    <path d="M9.5 13.5c.5 1.5 2.5 2 4 1" />
  </svg>
);

interface WhatsAppTabProps {
  settings: AdminSettings;
  updateSettings: (path: string[], value: any) => void;
  saveSettings: () => Promise<void>;
  isSaving: boolean;
  setEdgeFunctionError?: (error: string | null) => void;
}

export const WhatsAppTab: React.FC<WhatsAppTabProps> = ({
  settings,
  updateSettings,
  saveSettings,
  isSaving,
  setEdgeFunctionError
}) => {
  const { toast } = useToast();
  const [testMessage, setTestMessage] = useState("");
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [testSuccess, setTestSuccess] = useState<string | null>(null);
  const [diagnosticData, setDiagnosticData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string>("settings");

  // Helper function to validate and format phone number
  const formatPhoneNumber = (phone: string): string => {
    // Remove any non-numeric characters except the leading +
    const cleaned = phone.replace(/[^\d+]/g, '');
    
    // Ensure it starts with a +
    if (!cleaned.startsWith('+')) {
      return `+${cleaned}`;
    }
    
    return cleaned;
  };

  const sendTestWhatsAppMessage = async () => {
    // Clear previous test results
    setTestError(null);
    setTestSuccess(null);
    setDiagnosticData(null);
    
    if (setEdgeFunctionError) {
      setEdgeFunctionError(null);
    }
    
    // Validate phone number
    if (!settings.whatsapp.adminPhone || !settings.whatsapp.adminPhone.trim()) {
      setTestError("Please enter a valid phone number");
      toast({
        variant: "destructive",
        title: "Invalid Phone Number",
        description: "Please enter your WhatsApp phone number"
      });
      return;
    }

    // Validate message
    if (!testMessage || !testMessage.trim()) {
      setTestError("Please enter a message to send");
      toast({
        variant: "destructive",
        title: "Missing Message",
        description: "Please enter a test message to send"
      });
      return;
    }
    
    setIsSendingTest(true);
    
    try {
      // Format the phone number properly before sending
      const formattedPhone = formatPhoneNumber(settings.whatsapp.adminPhone);
      
      console.log("Sending test WhatsApp message to:", formattedPhone);
      console.log("Message:", testMessage);
      
      // Call the WhatsApp edge function to send the test message
      const { data, error } = await supabase.functions.invoke('send-whatsapp', {
        body: {
          body: testMessage,
          adminPhone: formattedPhone
        }
      });
      
      if (error) {
        console.error("WhatsApp test function error:", error);
        setTestError(`Error: ${error.message}`);
        setDiagnosticData({ error: error });
        
        if (setEdgeFunctionError) {
          setEdgeFunctionError(`Failed to call send-whatsapp function: ${error.message}`);
        }
        
        toast({
          variant: "destructive",
          title: "Function Error",
          description: error.message || "Error calling the WhatsApp function"
        });
        return;
      }
      
      console.log("WhatsApp test response:", data);
      
      // Save diagnostic data regardless of success/failure
      if (data.diagnostics) {
        setDiagnosticData(data.diagnostics);
      }
      
      if (data.success) {
        setTestSuccess(`Success! Test message sent to ${formattedPhone}`);
        setTestMessage("");
        toast({
          title: "Success",
          description: `Test WhatsApp message sent successfully`
        });
      } else {
        const errorMessage = data.error || 'Unknown error';
        setTestError(`Error: ${errorMessage}`);
        
        if (data.details) {
          console.error("WhatsApp API error details:", data.details);
          
          if (setEdgeFunctionError) {
            // Provide a more user-friendly error message 
            let detailedError = JSON.stringify(data.details);
            
            // Try to extract specific Twilio error messages if they exist
            if (data.details.message) {
              detailedError = data.details.message;
            } else if (data.details.more_info) {
              detailedError += ` More info: ${data.details.more_info}`;
            }
            
            setEdgeFunctionError(`Twilio API Error: ${detailedError}`);
          }
        }
        
        toast({
          variant: "destructive",
          title: "WhatsApp Test Failed",
          description: errorMessage
        });
      }
    } catch (error: any) {
      console.error("WhatsApp test error:", error);
      setTestError(`Error: ${error.message || 'Unknown error'}`);
      setDiagnosticData({ catchError: error });
      
      if (setEdgeFunctionError) {
        setEdgeFunctionError(`Unexpected error: ${error.message}`);
      }
      
      toast({
        variant: "destructive",
        title: "Test Failed",
        description: error.message || 'An unexpected error occurred'
      });
    } finally {
      setIsSendingTest(false);
    }
  };

  // Function to handle phone number input and format it properly
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    updateSettings(['whatsapp', 'adminPhone'], input);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>WhatsApp Notifications</CardTitle>
        <CardDescription>
          Configure WhatsApp notifications for admin alerts
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4 w-full">
            <TabsTrigger value="settings" className="flex-1">Settings</TabsTrigger>
            <TabsTrigger value="templates" className="flex-1">Message Templates</TabsTrigger>
            <TabsTrigger value="test" className="flex-1">Test</TabsTrigger>
          </TabsList>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch 
                id="whatsapp-enabled"
                checked={settings.whatsapp.enabled}
                onCheckedChange={(checked) => updateSettings(['whatsapp', 'enabled'], checked)}
              />
              <Label htmlFor="whatsapp-enabled">Enable WhatsApp Notifications</Label>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="admin-phone">Admin WhatsApp Number</Label>
              <div className="relative">
                <Input 
                  id="admin-phone"
                  value={settings.whatsapp.adminPhone}
                  onChange={handlePhoneNumberChange}
                  placeholder="Include country code, e.g., +12125551234"
                  className="pl-10"
                />
                <BrandWhatsapp className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">
                Include the country code with + prefix, e.g., +12125551234
              </p>
            </div>
            
            <Alert className="bg-blue-50 border-blue-200 text-blue-800">
              <Info className="h-4 w-4 text-blue-500" />
              <AlertTitle>WhatsApp API Requirements</AlertTitle>
              <AlertDescription className="text-blue-700 text-sm">
                Make sure you have:
                <ul className="list-disc pl-5 mt-1">
                  <li>A Twilio account with WhatsApp API access</li>
                  <li>Set the Twilio credentials in the Supabase dashboard</li>
                  <li>Your phone number must be registered with WhatsApp and Twilio</li>
                </ul>
                <div className="mt-2">
                  <a 
                    href="https://supabase.com/dashboard/project/ginefbvgxcobyuocxerb/settings/functions" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 inline-flex items-center"
                  >
                    Manage Twilio credentials in Supabase
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </div>
              </AlertDescription>
            </Alert>

            <Alert className="bg-amber-50 border-amber-200 text-amber-800">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <AlertTitle>WhatsApp Business Requirements</AlertTitle>
              <AlertDescription className="text-amber-700 text-sm">
                <p className="mb-2">For WhatsApp messaging to work:</p>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Your Twilio WhatsApp sandbox must be set up</li>
                  <li>The recipient must have joined your sandbox by sending the keyword to your Twilio number</li>
                  <li>For production use, you need an approved WhatsApp Business Profile</li>
                </ol>
                <div className="mt-3">
                  <a 
                    href="https://www.twilio.com/docs/whatsapp/quickstart" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 inline-flex items-center"
                  >
                    Twilio WhatsApp Setup Guide
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </div>
              </AlertDescription>
            </Alert>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="template-booking">New Booking Notification</Label>
              <Textarea
                id="template-booking"
                value={settings.whatsapp.templates.booking}
                onChange={(e) => updateSettings(['whatsapp', 'templates', 'booking'], e.target.value)}
                placeholder="New booking received..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="template-cancellation">Cancellation Notification</Label>
              <Textarea
                id="template-cancellation"
                value={settings.whatsapp.templates.cancellation}
                onChange={(e) => updateSettings(['whatsapp', 'templates', 'cancellation'], e.target.value)}
                placeholder="Booking cancelled..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="template-review">New Review Notification</Label>
              <Textarea
                id="template-review"
                value={settings.whatsapp.templates.review}
                onChange={(e) => updateSettings(['whatsapp', 'templates', 'review'], e.target.value)}
                placeholder="New review received..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="template-signup">New Signup Notification</Label>
              <Textarea
                id="template-signup"
                value={settings.whatsapp.templates.signup}
                onChange={(e) => updateSettings(['whatsapp', 'templates', 'signup'], e.target.value)}
                placeholder="New user signup..."
                rows={2}
              />
            </div>
          </TabsContent>

          {/* Test Tab */}
          <TabsContent value="test" className="space-y-4">
            <Alert className="mb-4 bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-500" />
              <AlertTitle>Testing WhatsApp Messages</AlertTitle>
              <AlertDescription className="text-blue-700">
                <p className="mb-2">Before testing, ensure that:</p>
                <ol className="list-decimal pl-5 space-y-1 mb-3">
                  <li>You've set up Twilio credentials in the Supabase dashboard</li>
                  <li>Your recipient number has opted into your Twilio WhatsApp sandbox</li>
                  <li>Format numbers with country code (e.g., +1234567890)</li>
                </ol>
                <p>Note: Even with a "Success" response, WhatsApp messages may be queued or filtered by Twilio.</p>
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="test-message">Test Message</Label>
              <Textarea 
                id="test-message"
                placeholder="Enter a test message to send"
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                rows={2}
              />
            </div>
            
            <Button 
              onClick={sendTestWhatsAppMessage}
              disabled={isSendingTest || !settings.whatsapp.adminPhone}
              className="w-full"
            >
              {isSendingTest ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Test Message
                </>
              )}
            </Button>
            
            {testSuccess && (
              <Alert className="bg-green-50 border-green-200">
                <Check className="h-4 w-4 text-green-600" />
                <AlertTitle className="text-green-800">Success</AlertTitle>
                <AlertDescription className="text-green-700">
                  {testSuccess}
                  <p className="mt-2 text-sm">
                    The message has been accepted by Twilio and queued for delivery.
                    If you don't receive it, please check your Twilio logs and make sure your
                    phone number has joined the Twilio sandbox.
                  </p>
                </AlertDescription>
              </Alert>
            )}
            
            {testError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>
                  {testError}
                </AlertDescription>
              </Alert>
            )}
            
            {diagnosticData && (
              <Collapsible className="w-full border rounded-md p-2">
                <CollapsibleTrigger asChild>
                  <Button variant="outline" size="sm" className="flex items-center justify-between w-full">
                    <span className="flex items-center">
                      <Bug className="h-4 w-4 mr-2" />
                      Diagnostic Information
                    </span>
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2">
                  <div className="bg-slate-950 text-slate-50 p-3 rounded-md text-xs overflow-x-auto">
                    <pre>{JSON.stringify(diagnosticData, null, 2)}</pre>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </TabsContent>
        </Tabs>
        
        <Button 
          onClick={saveSettings} 
          disabled={isSaving}
          className="mt-4"
        >
          {isSaving ? "Saving..." : "Save WhatsApp Settings"}
        </Button>
      </CardContent>
    </Card>
  );
};
